import { Labeled } from '@components/form/Labeled'
import { useCreateSettlement } from '@services/settlements/mutations/useCreateSettlement'
import { useSuggestedPeriods } from '@services/settlements/queries/useSuggestedPeriods'
import { Plus } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { formatDate } from '@/features/settlements/components/format'
import { Button } from '@/components/ui/button'
import { DatePicker } from '@/components/ui/date-picker'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'

export function SettlementCreateDialog({ flatId }: { flatId: string }) {
  const { t } = useTranslation('settlement')
  const navigate = useNavigate()

  const [open, setOpen] = useState(false)
  const [start, setStart] = useState<string | undefined>()
  const [end, setEnd] = useState<string | undefined>()
  const [importAdvances, setImportAdvances] = useState(true)

  const { data: suggestions } = useSuggestedPeriods({
    flat: flatId,
    enabled: open
  })
  const create = useCreateSettlement()

  const onSubmit = () => {
    if (!start || !end) return
    create.mutate(
      {
        flat: flatId,
        period_start: start,
        period_end: end,
        import_advances: importAdvances
      },
      {
        onSuccess: (settlement) => {
          setOpen(false)
          navigate(`/admin/settlements/${settlement.id}`)
        }
      }
    )
  }

  return (
    <>
      <Button size="sm" onClick={() => setOpen(true)}>
        <Plus className="mr-1.5 h-4 w-4" />
        {t('list.create')}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('create.title')}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <p className="m-0 mb-1 text-sm font-medium text-ink">
                {t('create.suggested')}
              </p>
              <p className="m-0 mb-2 text-xs text-muted-foreground">
                {t('create.suggested_hint')}
              </p>

              {suggestions && suggestions.length > 0 ? (
                <div className="space-y-1.5">
                  {suggestions.map((period) => {
                    const selected =
                      start === period.period_start && end === period.period_end
                    return (
                      <button
                        key={`${period.period_start}-${period.period_end}`}
                        type="button"
                        disabled={period.exists}
                        onClick={() => {
                          setStart(period.period_start)
                          setEnd(period.period_end)
                        }}
                        className={cn(
                          'flex w-full items-center justify-between rounded-xl border px-3 py-2 text-left text-sm transition-colors',
                          period.exists
                            ? 'cursor-not-allowed border-ink/[0.06] text-muted-foreground'
                            : selected
                            ? 'border-primary bg-primary/[0.06] text-ink'
                            : 'border-ink/[0.08] text-ink hover:border-primary/40'
                        )}
                      >
                        <span>
                          {formatDate(period.period_start)} –{' '}
                          {formatDate(period.period_end)}
                        </span>
                        {period.exists && (
                          <span className="text-xs">{t('create.exists')}</span>
                        )}
                      </button>
                    )
                  })}
                </div>
              ) : (
                <p className="m-0 text-sm text-muted-foreground">
                  {t('create.custom')}
                </p>
              )}
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <Labeled label={t('create.period_start')}>
                <DatePicker value={start} onChange={setStart} />
              </Labeled>
              <Labeled label={t('create.period_end')}>
                <DatePicker value={end} onChange={setEnd} />
              </Labeled>
            </div>

            <Labeled label={t('create.import_advances')}>
              <Switch
                checked={importAdvances}
                onCheckedChange={setImportAdvances}
              />
            </Labeled>

            <div className="flex justify-end">
              <Button
                onClick={onSubmit}
                disabled={!start || !end || create.isPending}
              >
                {t('create.submit')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
